// Settings › Awqaf donations — every field of settings/donations (see AWQAF_DONATION_PLAN.md).
// Writes with set({merge:true}); createDonation caches the doc for 5 minutes,
// the customer app reads it live to show/hide the donation card/sheet.
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import { LangPair, SettingList, SettingRow, SettingsCard, StatusPill } from './SettingsLayout';

// Keep in sync with DONATION_SETTINGS_DEFAULTS in functions/donation/index.js
const DEFAULTS = {
  enabled: false, presets: [5, 10, 20],
  minAmount: 1, maxAmount: 10000, maxPerHour: 5, afterOrderPromptDays: 30,
  titleEn: 'Donate to Awqaf', titleAr: 'تبرّع للأوقاف',
  descriptionEn: 'Your donation goes directly to Awqaf Dubai.', descriptionAr: 'يذهب تبرعك مباشرةً إلى أوقاف دبي.',
};
const textToList = (t) => String(t || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

const DonationSettings = () => {
  const [s, setS] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [presetsText, setPresetsText] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    firestore().collection('settings').doc('donations').get().then((snap) => {
      const d = snap.exists ? snap.data() : {};
      const merged = { ...DEFAULTS, ...d };
      setS(merged);
      setPresetsText((merged.presets || []).join(', '));
      setUpdatedAt(d.updatedAt || null);
    }).catch((e) => createNotification('error', (e && e.message) || 'Could not load settings', 'Donations'))
      .finally(() => setLoading(false));
  }, []);

  const set = useCallback((k, v) => { setS((prev) => ({ ...prev, [k]: v })); setDirty(true); }, []);
  const presets = textToList(presetsText).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  const minAmount = Number(s.minAmount) || 1;
  const maxAmount = Number(s.maxAmount) || DEFAULTS.maxAmount;
  const badPresets = presets.filter((p) => p < minAmount || p > maxAmount);
  const invalid = minAmount > maxAmount || badPresets.length > 0 || presets.length === 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!s.enabled,
        // test-customer rollout removed (2026-10-15) — drop the old field
        testUserIds: firestore.FieldValue.delete(),
        presets,
        minAmount,
        maxAmount,
        maxPerHour: Number(s.maxPerHour) || DEFAULTS.maxPerHour,
        afterOrderPromptDays: s.afterOrderPromptDays === '' || s.afterOrderPromptDays == null ? DEFAULTS.afterOrderPromptDays : Math.max(0, Number(s.afterOrderPromptDays) || 0),
        titleEn: String(s.titleEn || '').trim() || DEFAULTS.titleEn,
        titleAr: String(s.titleAr || '').trim() || DEFAULTS.titleAr,
        descriptionEn: String(s.descriptionEn || '').trim(),
        descriptionAr: String(s.descriptionAr || '').trim(),
        updatedAt: Date.now(),
      };
      await firestore().collection('settings').doc('donations').set(payload, { merge: true });
      setUpdatedAt(payload.updatedAt);
      setDirty(false);
      createNotification('success', 'Donation settings saved. The app updates immediately; payment creation within 5 minutes.', 'Settings');
    } catch (e) {
      createNotification('error', (e && e.message) || 'Save failed', 'Settings');
    } finally { setSaving(false); }
  };

  // One numeric setting row: label + help left, input with its unit right.
  const num = (label, key, help, unit, step = 1) => (
    <SettingRow label={label} help={help}>
      <InputGroup>
        <Input type="number" step={step} min="0" value={s[key] == null ? '' : s[key]} onChange={(e) => set(key, e.target.value)} />
        {unit ? <InputGroupAddon addonType="append"><InputGroupText>{unit}</InputGroupText></InputGroupAddon> : null}
      </InputGroup>
    </SettingRow>
  );
  const textInput = (key, rtl) => <Input type="text" dir={rtl ? 'rtl' : undefined} value={s[key] || ''} onChange={(e) => set(key, e.target.value)} />;

  if (loading) return <div className="loading" />;

  const footer = (
    <>
      <span className="st-foot-note">{updatedAt ? `Last saved ${new Date(Number(updatedAt)).toLocaleString()}` : 'Never saved'}{dirty ? ' · unsaved changes' : ''}</span>
      <Button color="primary" disabled={saving || !dirty || invalid} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </>
  );

  return (
    <SettingsCard
      title="Awqaf donations"
      description="Let customers donate to Awqaf Dubai from the app (Home, Profile and after placing an order). Payments go through DubaiPay straight to Awqaf — Chill never holds the money."
      status={<StatusPill on={!!s.enabled} offLabel="Off" onLabel="Live for everyone" />}
      footer={footer}
    >
      <div className="st-section">
        <div className="st-section-title">Rollout</div>
        <div className="st-switch-row">
          <div className="st-switch-text">
            <b>Donations {s.enabled ? 'are on' : 'are off'}</b>
            <span>Master switch. While on, every customer sees the donation card and sheet. While off the app hides them and no payment can be created.</span>
          </div>
          <Switch className="custom-switch custom-switch-primary" checked={!!s.enabled} onChange={(v) => set('enabled', v)} />
        </div>
      </div>

      <div className="st-section">
        <div className="st-section-title">Amounts</div>
        <SettingList>
          <SettingRow label="Preset amounts" help='Comma-separated amounts shown in the app, e.g. 5, 10, 20. An "Other amount" field is always shown too.'>
            <InputGroup>
              <Input type="text" value={presetsText} onChange={(e) => { setPresetsText(e.target.value); setDirty(true); }} />
              <InputGroupAddon addonType="append"><InputGroupText>AED</InputGroupText></InputGroupAddon>
            </InputGroup>
            {badPresets.length ? <div className="st-inline-error">Outside min/max: {badPresets.join(', ')}</div> : null}
          </SettingRow>
          {num('Minimum amount', 'minAmount', 'Smallest donation a customer can make.', 'AED')}
          {num('Maximum amount', 'maxAmount', 'Largest donation a customer can make.', 'AED')}
          {num('Attempts per hour', 'maxPerHour', 'Per customer, to stop abuse.', 'per hour')}
        </SettingList>
        {minAmount > maxAmount ? <div className="text-danger" style={{ marginTop: 8, fontSize: 12 }}>Minimum is above maximum.</div> : null}
      </div>

      <div className="st-section">
        <div className="st-section-title">After-order prompt</div>
        <SettingList>
          {num('Show at most once every', 'afterOrderPromptDays', 'The donation sheet that opens after a customer places an order. Skipped for customers who donated within this window. 0 = never show it after orders; the Home and Profile cards are always available.', 'days')}
        </SettingList>
      </div>

      <div className="st-section">
        <div className="st-section-title">Copy shown in the app</div>
        <SettingList>
          <SettingRow wide label="Title" help="Heading of the donation card and sheet.">
            <LangPair left={textInput('titleEn')} right={textInput('titleAr', true)} />
          </SettingRow>
          <SettingRow wide label="Description" help="One line under the title.">
            <LangPair left={textInput('descriptionEn')} right={textInput('descriptionAr', true)} />
          </SettingRow>
        </SettingList>
      </div>
    </SettingsCard>
  );
};

export default DonationSettings;
